import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { useMarketLiquidity } from '../../hooks/useMarketLiquidityStore';
import { ProductType } from '../../hooks/useProducts';
import { cn } from '../../lib/classNameHelpers';
import {
  centerRangeWithBoundaries,
  domain,
  Domain,
  getDomainRange,
  getMinZoomRange,
  isAtMaxZoomIn,
  isAtMaxZoomOut,
  processDepthData,
} from '../../lib/depthChartHelpers';
import { DepthData } from '../../lib/depthDataHelpers';
import { formatDollar } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { getDecimalPlaces } from '../../lib/math';
import { Button } from '../catalyst-ui-kit/button/button';
import { DepthChartSkeleton } from './DebtChartSkeleton';
import { DepthChartTooltip } from './DepthChartTooltip';

const MARGIN = 8; // AreaChart margin
const ZOOM_FACTOR = 0.05; // Amount to zoom per scroll step
const ZOOM_BUTTON_STEP = 0.2; // Amount to zoom when clicking zoom buttons
const ZOOM_HOLD_DELAY = 300; // Delay before continuous zoom starts (ms)
const ZOOM_REPEAT_INTERVAL = 100; // Interval for continuous zoom (ms)

export const DepthChartUi: React.FC<{ data: DepthData[]; tickSize: bigint }> = ({
  data,
  tickSize,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Process depth data into values required for displaying chart
  const {
    bidData,
    askData,
    minPrice,
    maxPrice,
    priceRange,
    spread,
    midPrice,
    defaultMinPrice,
    defaultMaxPrice,
  } = useMemo(() => processDepthData(data), [data]);

  // Track if the user has zoomed manually for showing Reset button
  const [hasZoomed, setHasZoomed] = useState(false);

  // Current zoom domain
  const [zoomDomain, setZoomDomain] = useState<Domain>(domain(defaultMinPrice, defaultMaxPrice));

  // Track max zoom states for button disabling
  const [atMaxZoomIn, setAtMaxZoomIn] = useState(false);
  const [atMaxZoomOut, setAtMaxZoomOut] = useState(false);

  // Store zoom domain range as a ref to avoid a render loop
  const zoomDomainRangeRef = useRef<number>(getDomainRange(zoomDomain));
  useEffect(() => {
    zoomDomainRangeRef.current = getDomainRange(zoomDomain);
  }, [zoomDomain]);

  // Re-center and update zoom when the data changes
  useEffect(() => {
    // If fully zoomed out we update zoom domain directly, otherwise we need to
    // calculate new zoom domain based on the current zoom range
    if (atMaxZoomOut) {
      setZoomDomain(domain(minPrice, maxPrice));
    } else {
      let targetRange: number;
      if (atMaxZoomIn) {
        targetRange = getMinZoomRange(spread);
      } else {
        // Maintain current zoom level relative to default
        const defaultRange = defaultMaxPrice - defaultMinPrice;
        const currentRange = zoomDomainRangeRef.current;

        const zoomRatio = currentRange / defaultRange;
        targetRange = defaultRange * zoomRatio;
      }

      // Center the range on mid price with boundary constraints
      setZoomDomain(centerRangeWithBoundaries(targetRange, midPrice, minPrice, maxPrice));
    }
  }, [
    midPrice,
    minPrice,
    maxPrice,
    priceRange,
    defaultMinPrice,
    defaultMaxPrice,
    spread,
    atMaxZoomOut,
    atMaxZoomIn,
  ]);

  // Zoom in or out by the provided factor
  const applyZoom = useCallback(
    (zoomFactor: number) => {
      const currentRange = zoomDomainRangeRef.current;
      const minZoomRange = getMinZoomRange(spread);

      // Calculate new zoom range
      const newRange = Math.max(
        minZoomRange,
        Math.min(priceRange, currentRange * (1 + zoomFactor))
      );

      const newDomain = centerRangeWithBoundaries(newRange, midPrice, minPrice, maxPrice);

      setZoomDomain(newDomain);
      setHasZoomed(true);

      // Check both zoom extremes
      const isMaxZoomOut = isAtMaxZoomOut(newDomain, minPrice, maxPrice);
      const isMaxZoomIn = isAtMaxZoomIn(newRange, spread);

      // Update state for button disabling
      setAtMaxZoomOut(isMaxZoomOut);
      setAtMaxZoomIn(isMaxZoomIn);
    },
    [priceRange, spread, midPrice, minPrice, maxPrice]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const direction = e.deltaY > 0 ? 1 : -1;
      applyZoom(direction * ZOOM_FACTOR);
    },
    [applyZoom]
  );

  // Add wheel event listener manually so that we can set `passive: false` to
  // prevent the whole page from scrolling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleReset = useCallback(() => {
    setZoomDomain(domain(defaultMinPrice, defaultMaxPrice));
    setHasZoomed(false);
    setAtMaxZoomOut(false);
    setAtMaxZoomIn(false);
  }, [defaultMinPrice, defaultMaxPrice]);

  // Shared refs for zoom button intervals
  const zoomIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleZoomEnd = useCallback(() => {
    // Clear timeout and interval
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = null;
    }
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current);
      zoomIntervalRef.current = null;
    }
  }, []);

  // Button zoom handler that applies zoom on press, then if still pressing
  // after 500ms, calls zoom every 100ms.
  const handleZoomStart = useCallback(
    (zoomFactor: number) => {
      // Clear any existing zoom
      handleZoomEnd();

      // Apply zoom immediately
      applyZoom(zoomFactor);

      // Set timeout for delay before starting repeat
      zoomTimeoutRef.current = setTimeout(() => {
        // Start repeating at specified interval
        zoomIntervalRef.current = setInterval(() => {
          applyZoom(zoomFactor);
        }, ZOOM_REPEAT_INTERVAL);
      }, ZOOM_HOLD_DELAY);
    },
    [applyZoom, handleZoomEnd]
  );

  const handleZoomInStart = useCallback(() => {
    handleZoomStart(-ZOOM_BUTTON_STEP);
  }, [handleZoomStart]);

  const handleZoomOutStart = useCallback(() => {
    handleZoomStart(ZOOM_BUTTON_STEP);
  }, [handleZoomStart]);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      handleZoomEnd();
    };
  }, [handleZoomEnd]);

  const priceDecimals = getDecimalPlaces(tickSize);

  return (
    <div className="relative size-full">
      <ResponsiveContainer
        width="98%"
        height="100%"
        className="mx-auto size-full select-none"
        ref={containerRef}
      >
        <AreaChart margin={{ top: MARGIN, right: MARGIN, left: MARGIN, bottom: MARGIN }}>
          <defs>
            <linearGradient id="askStroke" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--border-borderError)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="var(--border-borderError)" stopOpacity={0.2} />
            </linearGradient>

            <linearGradient id="bidStroke" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-borderSuccess)" stopOpacity={0.6} />
              <stop offset="100%" stopColor="var(--border-borderSuccess)" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="price"
            type="number"
            domain={zoomDomain}
            interval="preserveStartEnd"
            tickCount={10}
            axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
            tickFormatter={(value) =>
              formatDollar(Gwei.toGwei(value), {
                maximumFractionDigits: priceDecimals,
                minimumFractionDigits: priceDecimals,
              })
            }
            tickLine={false}
            tick={{
              fill: 'var(--content-contentSecondary)',
              dy: MARGIN,
              fontSize: '10px',
            }}
            allowDataOverflow={true}
          />
          <Area
            type="stepAfter"
            dataKey="depth"
            stroke="var(--border-borderError)"
            strokeWidth={1}
            fill="url(#askStroke)"
            fillOpacity={0.65}
            data={askData}
            activeDot={false}
            isAnimationActive={true}
            animationDuration={200}
          />
          <Area
            type="stepAfter"
            dataKey="depth"
            stroke="var(--border-borderSuccess)"
            strokeWidth={1}
            fill="url(#bidStroke)"
            fillOpacity={0.65}
            data={bidData}
            activeDot={false}
            isAnimationActive={true}
            animationDuration={200}
          />
          <Tooltip content={<DepthChartTooltip priceDecimals={priceDecimals} />} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="absolute right-2 top-2 z-10 flex gap-1">
        <Button
          color="tertiary"
          size="xs"
          onClick={handleReset}
          className={cn('transition-opacity', hasZoomed ? 'opacity-100' : 'opacity-0')}
        >
          Reset
        </Button>
        <Button
          color="tertiary"
          size="xs"
          disabled={atMaxZoomOut}
          onMouseDown={handleZoomOutStart}
          onMouseUp={handleZoomEnd}
          onMouseLeave={handleZoomEnd}
          onTouchStart={handleZoomOutStart}
          onTouchEnd={handleZoomEnd}
        >
          −
        </Button>
        <Button
          color="tertiary"
          size="xs"
          disabled={atMaxZoomIn}
          onMouseDown={handleZoomInStart}
          onMouseUp={handleZoomEnd}
          onMouseLeave={handleZoomEnd}
          onTouchStart={handleZoomInStart}
          onTouchEnd={handleZoomEnd}
        >
          +
        </Button>
      </div>
    </div>
  );
};

export const DepthChart: React.FC<{ product: ProductType }> = ({ product }) => {
  const { marketDepth, liquidityData } = useMarketLiquidity(product.id);

  if (!marketDepth || !liquidityData) return <DepthChartSkeleton />;

  if (marketDepth.length === 0) {
    return (
      <div className="flex items-center justify-center text-contentPrimary">
        No market depth data available.
      </div>
    );
  }

  return <DepthChartUi data={marketDepth} tickSize={product.tickSize} />;
};
